// libraries.
import { Form, Row, Col } from "react-bootstrap";
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFieldArrayReturn,
} from "react-hook-form";

// interfaces.
import { RawTool } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// types.
type ContextVar = { label: string; value: string };

// styles.
import { StyledRulesModalForm } from "../../styles";

// constants.
import { CONTEXT_VARIABLES, CONDITION_OPERATORS } from "../rulesFormConstants";

// types + sub-components.
import { FormValues, SubConditionValues, ActionValues } from "./useCreateRuleForm";
import { ActionEditor } from "./ActionEditor";

// types.
type SubRutaOption = { id: number; name: string };

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  tools: RawTool[];
  contextVars: ContextVar[];
  isLoadingTools: boolean;
  subConditionArray: UseFieldArrayReturn<FormValues, "subConditions">;
  actionArray: UseFieldArrayReturn<FormValues, "actions">;
  emptySubCondition: SubConditionValues;
  emptyAction: ActionValues;
  isEditMode?: boolean;
  subRutasOptions?: SubRutaOption[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateRuleFields = ({
  register,
  errors,
  watch,
  setValue,
  tools,
  contextVars,
  isLoadingTools,
  subConditionArray,
  actionArray,
  emptySubCondition,
  emptyAction,
  isEditMode = false,
  subRutasOptions = [],
}: Props) => {
  const conditionMode = watch("conditionMode");
  const conditionLeft = watch("conditionLeft");
  const subConditions = watch("subConditions") ?? [];
  const effectiveVars: ContextVar[] = contextVars.length > 0 ? contextVars : CONTEXT_VARIABLES;
  const allActionOutputs = (watch("actions") ?? []).map((a) => a.output);

  // Si el valor guardado no está en las vars disponibles, lo agregamos como
  // opción de fallback para que el <select> pueda mostrarlo de inmediato.
  const varValues = new Set(effectiveVars.map((v) => v.value));
  const conditionLeftFallback =
    conditionLeft && !varValues.has(conditionLeft) ? conditionLeft : null;

  return (
    <StyledRulesModalForm className="d-flex flex-column gap-3">

      {/* ── Clasificación: Sub-ruta y tipo ──────────────────────────────── */}
      <Row className="g-3">
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Sub-ruta</Form.Label>
            <Form.Select
              size="sm"
              {...register("sub_ruta_id", { required: "Requerido", valueAsNumber: true })}
              isInvalid={!!errors.sub_ruta_id}
            >
              {subRutasOptions.length === 0 && (
                <option value="">— Sin sub-rutas —</option>
              )}
              {subRutasOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.sub_ruta_id?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group>
            <Form.Label>Tipo</Form.Label>
            <Form.Select
              size="sm"
              {...register("state_route", { required: "Requerido", valueAsNumber: true })}
              isInvalid={!!errors.state_route}
            >
              <option value={1}>🏆 Winner — Producción</option>
              <option value={2}>🥊 Contender — Experimento</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.state_route?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* ── Identificación ──────────────────────────────────────────────── */}
      <Row className="g-3">
        <Col xs={9}>
          <Form.Group>
            <Form.Label>ID de la regla</Form.Label>
            <Form.Control
              size="sm"
              placeholder="ej: colombia_bono_nuevo"
              disabled={isEditMode}
              {...register("rule_id", {
                required: "Requerido",
                pattern: {
                  value: /^[a-z0-9_]+$/,
                  message: "Solo letras minúsculas, números y guiones bajos",
                },
              })}
              isInvalid={!!errors.rule_id}
            />
            <Form.Control.Feedback type="invalid">{errors.rule_id?.message}</Form.Control.Feedback>
            <Form.Text className="text-muted" style={{ fontSize: 11 }}>
              Solo <code>a-z</code>, <code>0-9</code> y <code>_</code>. Ej: <code>colombia_bono_nuevo</code>
            </Form.Text>
          </Form.Group>
        </Col>
        <Col xs={3}>
          <Form.Group>
            <Form.Label>Prioridad</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={0}
              max={999}
              {...register("priority", {
                required: "Requerido",
                valueAsNumber: true,
                min: { value: 0, message: "≥ 0" },
                max: { value: 999, message: "≤ 999" },
              })}
              isInvalid={!!errors.priority}
            />
            <Form.Control.Feedback type="invalid">{errors.priority?.message}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group>
        <Form.Label>Nombre</Form.Label>
        <Form.Control size="sm" placeholder="Nombre descriptivo de la regla" {...register("name", { required: "Requerido" })} isInvalid={!!errors.name} />
        <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group>
        <Form.Label>Descripción</Form.Label>
        <Form.Control as="textarea" rows={2} size="sm" placeholder="Descripción de la regla" {...register("description")} />
      </Form.Group>

      <Form.Check type="switch" label="Regla activa al crear" {...register("is_active")} />

      {/* ── Condición ───────────────────────────────────────────────────── */}
      <hr className="my-1" />
      <p className="mb-1 fw-semibold" style={{ fontSize: 13 }}>Condición</p>

      <Form.Group>
        <Form.Label>Tipo de condición</Form.Label>
        <Form.Select size="sm" {...register("conditionMode")}>
          <option value="simple">Simple</option>
          <option value="and">Compuesta — AND (todas deben cumplirse)</option>
          <option value="or">Compuesta — OR (al menos una debe cumplirse)</option>
        </Form.Select>
      </Form.Group>

      {conditionMode === "simple" && (
        <Row className="g-2">
          <Col xs={5}>
            <Form.Select size="sm" {...register("conditionLeft", { required: "Requerido" })} isInvalid={!!errors.conditionLeft}>
              <option value="">Variable...</option>
              {conditionLeftFallback && (
                <option value={conditionLeftFallback}>{conditionLeftFallback}</option>
              )}
              {effectiveVars.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </Form.Select>
          </Col>
          <Col xs={4}>
            <Form.Select size="sm" {...register("conditionType", { required: "Requerido" })}>
              {CONDITION_OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
            </Form.Select>
          </Col>
          <Col xs={3}>
            <Form.Control size="sm" placeholder="Valor" {...register("conditionRight", { required: "Requerido" })} isInvalid={!!errors.conditionRight} />
          </Col>
        </Row>
      )}

      {(conditionMode === "and" || conditionMode === "or") && (
        <div className="d-flex flex-column gap-2">
          {subConditionArray.fields.map((field, idx) => {
            const subLeft = subConditions[idx]?.left ?? "";
            const subLeftFallback = subLeft && !varValues.has(subLeft) ? subLeft : null;
            return (
            <Row key={field.id} className="g-2 align-items-center">
              <Col xs={4}>
                <Form.Select size="sm" {...register(`subConditions.${idx}.left`, { required: true })}>
                  <option value="">Variable...</option>
                  {subLeftFallback && (
                    <option value={subLeftFallback}>{subLeftFallback}</option>
                  )}
                  {effectiveVars.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={4}>
                <Form.Select size="sm" {...register(`subConditions.${idx}.type`)}>
                  {CONDITION_OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={3}>
                <Form.Control size="sm" placeholder="Valor" {...register(`subConditions.${idx}.right`, { required: true })} />
              </Col>
              <Col xs={1} className="text-end">
                {subConditionArray.fields.length > 1 && (
                  <button type="button" className="btn-remove-item" onClick={() => subConditionArray.remove(idx)}>✕</button>
                )}
              </Col>
            </Row>
            );
          })}
          <button type="button" className="btn-add-item" onClick={() => subConditionArray.append(emptySubCondition)}>
            + Agregar condición
          </button>
        </div>
      )}

      {/* ── Acciones ────────────────────────────────────────────────────── */}
      <hr className="my-1" />
      <p className="mb-1 fw-semibold" style={{ fontSize: 13 }}>Acciones</p>

      {isLoadingTools && <p className="text-muted" style={{ fontSize: 12 }}>Cargando tools disponibles...</p>}

      {actionArray.fields.map((field, idx) => (
        <ActionEditor
          key={field.id}
          idx={idx}
          register={register}
          watch={watch}
          setValue={setValue}
          tools={tools}
          contextVars={effectiveVars}
          previousOutputs={allActionOutputs.slice(0, idx).filter(Boolean)}
          canRemove={actionArray.fields.length > 1}
          isFirst={idx === 0}
          isLast={idx === actionArray.fields.length - 1}
          onRemove={() => actionArray.remove(idx)}
          onMoveUp={() => actionArray.move(idx, idx - 1)}
          onMoveDown={() => actionArray.move(idx, idx + 1)}
        />
      ))}

      <button type="button" className="btn-add-item" onClick={() => actionArray.append(emptyAction)}>
        + Agregar acción
      </button>

    </StyledRulesModalForm>
  );
};
